import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircleIcon, DownloadIcon, FileIcon } from 'lucide-react';
import { toast } from 'sonner';

import { procurementApi as api } from 'api/procurement';
import { Alert, AlertDescription, AlertTitle } from 'shadcn/components/ui/Alert';
import { Badge } from 'shadcn/components/ui/Badge';
import { Button } from 'shadcn/components/ui/Button';
import { Input } from 'shadcn/components/ui/Input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';

function DocumentsTable({ poID }) {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    const fetchDocuments = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await api.getPurchaseOrderDocuments(poID);

            if (response.status === false) {
                setError(response.msg || 'Failed to load documents');
                setDocuments([]);
                return;
            }

            setDocuments(response.data.rows || []);
        } catch (err) {
            setError('An error occurred while loading documents');
            console.error('Error fetching documents:', err);
        } finally {
            setLoading(false);
        }
    }, [poID]);

    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);

    const handleDownload = useCallback(
        (documentID, documentURL) => {
            const fileName = documentURL.replace(/^.*[\\/]/, '');
            api.downloadPODocument(poID, documentID, fileName);
            toast.success('Downloading document...');
        },
        [poID],
    );

    const filteredDocuments = useMemo(() => {
        if (!searchQuery || searchQuery.trim() === '') {
            return documents;
        }

        const searchTerm = searchQuery.toLowerCase().trim();
        return documents.filter((doc) => {
            const searchableFields = [
                doc.id?.toString() || '',
                doc.sku?.toString() || '',
                doc.externalSku?.toString() || '',
                doc.name?.toString() || '',
                doc.notes?.toString() || '',
            ];

            return searchableFields.some((field) => field.toLowerCase().includes(searchTerm));
        });
    }, [documents, searchQuery]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="destructive">
                <AlertCircleIcon className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
            </Alert>
        );
    }

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <FileIcon className="h-4 w-4 text-gray-500" />
                    <h3 className="text-sm font-semibold">
                        Documents ({filteredDocuments.length}
                        {searchQuery && filteredDocuments.length !== documents.length && ` of ${documents.length}`})
                    </h3>
                </div>
            </div>

            {/* Search Bar */}
            <div className="w-full">
                <Input
                    type="text"
                    placeholder="Search across all columns..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full"
                />
            </div>

            {documents.length === 0 ? (
                <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-dashed">
                    <FileIcon className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                    <p className="text-sm">No documents uploaded yet</p>
                </div>
            ) : (
                <div className="rounded-md border">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-gray-50">
                                <TableHead className="font-semibold">Document ID</TableHead>
                                <TableHead className="font-semibold">SKU</TableHead>
                                <TableHead className="font-semibold">External SKU</TableHead>
                                <TableHead className="font-semibold">Item Name</TableHead>
                                <TableHead className="font-semibold">Label</TableHead>
                                <TableHead className="font-semibold text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredDocuments.length > 0 ? (
                                filteredDocuments.map((doc) => (
                                    <TableRow key={doc.id} className="hover:bg-gray-50">
                                        <TableCell>
                                            <span className="font-mono text-xs">{doc.id}</span>
                                        </TableCell>
                                        <TableCell>
                                            <span className="font-mono text-xs">{doc.sku || 'N/A'}</span>
                                        </TableCell>
                                        <TableCell>
                                            <span className="font-mono text-xs">{doc.externalSku || 'N/A'}</span>
                                        </TableCell>
                                        <TableCell className="max-w-[200px] truncate">{doc.name || 'N/A'}</TableCell>
                                        <TableCell>
                                            <Badge variant="outline">{doc.notes || 'N/A'}</Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {doc.documentURL ? (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleDownload(doc.id, doc.documentURL)}
                                                    className="gap-2"
                                                >
                                                    <DownloadIcon className="h-4 w-4" />
                                                    Download
                                                </Button>
                                            ) : (
                                                <span className="text-sm text-gray-400">No file</span>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={6} className="h-24 text-center text-gray-500">
                                        No documents match your search
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
            )}
        </div>
    );
}

export { DocumentsTable };
